import React from 'react';
import { ShieldCheck, Loader2, AlertCircle, Trophy, Info, RefreshCw } from 'lucide-react';

/**
 * Credibility Audit panel — pairwise head-to-head scoring.
 * Every bull is matched against every bear (5 × 5 = 25 matchups).
 * Each matchup is also rated for decisiveness (1=close call, 2=clear, 3=blowout).
 * Verdict is whoever wins more matchups — no TIE possible.
 */

const DIMENSION_LABEL = {
  source_primacy: 'Source primacy',
  recency: 'Recency',
  magnitude_fit: 'Magnitude vs. evidence',
  counterfactual: 'Counterfactual specificity',
};

const RecordBadge = ({ wins, losses, side }) => {
  // Color the badge to reflect dominance
  const isStrong = wins >= 4;
  const isWeak = wins <= 1;
  const sideAccent =
    side === 'BULL' ? 'hsl(var(--gb-green-dark))' : 'hsl(var(--gb-rust))';

  let bg, fg;
  if (isStrong) {
    bg = sideAccent;
    fg = side === 'BULL' ? 'hsl(var(--gb-green-accent))' : '#FF8B67';
  } else if (isWeak) {
    bg = 'hsl(var(--gb-surface-2))';
    fg = 'hsl(var(--gb-text-muted))';
  } else {
    bg = 'hsl(var(--gb-gold))';
    fg = 'hsl(var(--gb-navy))';
  }

  return (
    <span
      className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-mono font-semibold whitespace-nowrap"
      style={{ backgroundColor: bg, color: fg, minWidth: '54px' }}
      data-testid={`record-badge-${side.toLowerCase()}`}
      title={`Won ${wins} of 5 head-to-head matchups`}
    >
      {wins}W–{losses}L
    </span>
  );
};

const VerdictBar = ({ bullsWins, bearsWins }) => {
  const total = bullsWins + bearsWins; // always 25
  const bullsPct = total > 0 ? (bullsWins / total) * 100 : 50;

  return (
    <div className="space-y-3" data-testid="verdict-bar">
      <div className="flex justify-between items-baseline text-xs uppercase tracking-wider font-semibold">
        <span className="flex items-center gap-1.5" style={{ color: 'hsl(var(--gb-green-dark))' }}>
          🐂 GeoBulls{' '}
          <span className="font-mono normal-case font-semibold" data-testid="bulls-wins">
            {bullsWins} / 25
          </span>
        </span>
        <span className="flex items-center gap-1.5" style={{ color: 'hsl(var(--gb-rust))' }}>
          <span className="font-mono normal-case font-semibold" data-testid="bears-wins">
            {bearsWins} / 25
          </span>{' '}
          🐻 GeoBears
        </span>
      </div>
      <div
        className="w-full h-4 rounded-full overflow-hidden flex"
        style={{ backgroundColor: 'hsl(var(--gb-surface-2))' }}
      >
        <div
          className="h-full transition-all duration-700"
          style={{
            width: `${bullsPct}%`,
            backgroundColor: 'hsl(var(--gb-green-dark))',
          }}
          aria-hidden="true"
        />
        <div
          className="h-full transition-all duration-700"
          style={{
            width: `${100 - bullsPct}%`,
            backgroundColor: 'hsl(var(--gb-rust))',
          }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

const VerdictHeader = ({ verdict, bullsWins, bearsWins, avgDecisiveness }) => {
  const isBulls = verdict === 'BULLS';
  const winnerWins = isBulls ? bullsWins : bearsWins;
  const label = isBulls ? '🐂 BULLS more credible' : '🐻 BEARS more credible';
  const color = isBulls ? 'hsl(var(--gb-green-dark))' : 'hsl(var(--gb-rust))';

  // Qualifier from average decisiveness
  let qualifier;
  if (avgDecisiveness >= 2.3) qualifier = 'Decisive edge';
  else if (avgDecisiveness >= 1.7) qualifier = 'Clear edge';
  else qualifier = 'Directional edge';

  return (
    <div className="text-center" data-testid="verdict-header">
      <div className="inline-flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.32em] font-medium" style={{ color: 'hsl(var(--gb-text-muted))' }}>
        <Trophy className="h-3 w-3" aria-hidden="true" />
        Verdict
      </div>
      <h3
        className="text-2xl sm:text-3xl font-semibold leading-tight"
        style={{ fontFamily: 'var(--font-display)', color }}
        data-testid="verdict-label"
      >
        {label}
      </h3>
      <p className="text-sm mt-1.5 font-mono" style={{ color: 'hsl(var(--gb-text-muted))' }}>
        Won <span className="font-semibold" style={{ color }}>{winnerWins}</span> of 25 head-to-head matchups
        {' · '}
        <span style={{ color }}>{qualifier}</span>
      </p>
    </div>
  );
};

const DirectionalNote = () => (
  <div
    className="flex items-start gap-2 rounded-md p-3 text-xs leading-relaxed"
    style={{
      backgroundColor: 'hsl(var(--gb-surface-2))',
      color: 'hsl(var(--gb-text))',
    }}
    data-testid="directional-note"
  >
    <Info className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(var(--gb-gold))' }} aria-hidden="true" />
    <span>
      <strong style={{ color: 'hsl(var(--gb-navy))' }}>Directional verdict.</strong>{' '}
      The winner has a wide lead in count but most matchups were close calls — read this as a
      directional bias, not a dominant case.
    </span>
  </div>
);

const StatStrip = ({ avgDecisiveness, dimensionBreakdown }) => {
  const total = Object.values(dimensionBreakdown || {}).reduce((s, v) => s + v, 0) || 25;
  const ordered = ['source_primacy', 'recency', 'magnitude_fit', 'counterfactual'];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
      {/* Avg decisiveness */}
      <div
        className="rounded-md p-3 border"
        style={{
          backgroundColor: 'hsl(var(--gb-surface-2))',
          borderColor: 'hsl(var(--gb-border))',
        }}
      >
        <div className="text-[10px] uppercase tracking-wider mb-1.5 font-semibold" style={{ color: 'hsl(var(--gb-text-muted))' }}>
          Average decisiveness
        </div>
        <div className="flex items-baseline gap-2">
          <span
            className="text-lg font-mono font-semibold"
            style={{ color: 'hsl(var(--gb-navy))' }}
            data-testid="avg-decisiveness"
          >
            {avgDecisiveness?.toFixed(2)} / 3.00
          </span>
          <span style={{ color: 'hsl(var(--gb-text-muted))' }}>
            ({avgDecisiveness >= 2.3 ? 'mostly decisive' : avgDecisiveness >= 1.7 ? 'mostly clear' : 'mostly close'})
          </span>
        </div>
      </div>

      {/* Dimension breakdown */}
      <div
        className="rounded-md p-3 border"
        style={{
          backgroundColor: 'hsl(var(--gb-surface-2))',
          borderColor: 'hsl(var(--gb-border))',
        }}
        data-testid="dimension-breakdown"
      >
        <div className="text-[10px] uppercase tracking-wider mb-1.5 font-semibold" style={{ color: 'hsl(var(--gb-text-muted))' }}>
          Deciding dimension (of 25)
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {ordered.map((k) => (
            <span key={k} className="flex items-baseline gap-1">
              <span style={{ color: 'hsl(var(--gb-navy))' }}>{DIMENSION_LABEL[k]}</span>
              <span className="font-mono font-semibold" style={{ color: 'hsl(var(--gb-navy))' }}>
                {dimensionBreakdown?.[k] ?? 0}
              </span>
            </span>
          ))}
          <span className="opacity-50 text-[10px] italic" style={{ color: 'hsl(var(--gb-text-muted))' }}>
            (total: {total})
          </span>
        </div>
      </div>
    </div>
  );
};

const RecordRow = ({ record, sideColor }) => (
  <tr className="border-t" style={{ borderColor: 'hsl(var(--gb-border))' }}>
    <td className="px-3 py-2.5 align-top text-xs font-mono whitespace-nowrap" style={{ color: 'hsl(var(--gb-text-muted))' }}>
      #{record.number}
    </td>
    <td className="px-3 py-2.5 align-top text-xs font-semibold uppercase tracking-wider whitespace-nowrap" style={{ color: sideColor }}>
      {record.side}
    </td>
    <td className="px-3 py-2.5 align-top">
      <RecordBadge wins={record.wins} losses={record.losses} side={record.side} />
    </td>
    <td className="px-3 py-2.5 align-top text-xs font-medium" style={{ color: 'hsl(var(--gb-navy))' }}>
      {record.label}
    </td>
    <td className="px-3 py-2.5 align-top text-xs italic leading-relaxed" style={{ color: 'hsl(var(--gb-text))' }}>
      “{record.top_quote}”
    </td>
  </tr>
);

const SectionHeader = () => (
  <div className="flex items-center gap-4 mb-4">
    <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
    <h3
      className="text-sm font-semibold tracking-[0.18em] uppercase flex items-center gap-2"
      style={{ color: 'hsl(var(--gb-text-muted))' }}
    >
      <ShieldCheck className="h-4 w-4" aria-hidden="true" />
      Credibility Audit
    </h3>
    <div className="h-px flex-1" style={{ backgroundColor: 'hsl(var(--gb-gold))' }} />
  </div>
);

const Skeleton = () => (
  <section data-testid="credibility-audit-loading">
    <SectionHeader />
    <div
      className="rounded-lg p-6 border-l-4 flex items-center justify-center gap-3"
      style={{
        backgroundColor: 'hsl(var(--gb-surface))',
        borderLeftColor: 'hsl(var(--gb-gold))',
        minHeight: '200px',
      }}
    >
      <Loader2
        className="h-5 w-5 animate-spin"
        style={{ color: 'hsl(var(--gb-gold))' }}
        aria-hidden="true"
      />
      <span className="text-sm" style={{ color: 'hsl(var(--gb-text-muted))' }}>
        Auditing arguments — running 25 head-to-head matchups…
      </span>
    </div>
  </section>
);

const ErrorBlock = ({ message, onRetry }) => (
  <section data-testid="credibility-audit-error">
    <SectionHeader />
    <div
      className="rounded-lg p-4 border-l-4 flex items-start gap-3"
      style={{
        backgroundColor: 'hsl(var(--gb-danger-surface))',
        borderLeftColor: 'hsl(var(--gb-danger-border))',
      }}
    >
      <AlertCircle
        className="h-5 w-5 flex-shrink-0 mt-0.5"
        style={{ color: 'hsl(var(--gb-danger-border))' }}
        aria-hidden="true"
      />
      <div className="text-sm flex-1" style={{ color: 'hsl(var(--gb-text))' }}>
        <p className="font-semibold mb-0.5" style={{ color: 'hsl(var(--gb-danger-border))' }}>
          Audit unavailable
        </p>
        <p className="mb-2" style={{ color: 'hsl(var(--gb-text-muted))' }}>
          {message ||
            'The credibility audit could not be generated for this report. The main analysis above is unaffected.'}
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            data-testid="audit-retry-button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2"
            style={{
              backgroundColor: 'hsl(var(--gb-navy))',
              color: 'hsl(var(--gb-primary-foreground))',
            }}
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry audit
          </button>
        )}
      </div>
    </div>
  </section>
);

const CredibilityAudit = ({ status, audit, error, onRetry }) => {
  if (status === 'loading') return <Skeleton />;
  if (status === 'error' || !audit) return <ErrorBlock message={error} onRetry={onRetry} />;

  const bullColor = 'hsl(var(--gb-green-dark))';
  const bearColor = 'hsl(var(--gb-rust))';

  return (
    <section data-testid="credibility-audit">
      <SectionHeader />

      <div
        className="rounded-lg p-5 sm:p-6 border-l-4 space-y-6"
        style={{
          backgroundColor: 'hsl(var(--gb-surface))',
          borderLeftColor: 'hsl(var(--gb-gold))',
        }}
      >
        {/* Verdict header */}
        <VerdictHeader
          verdict={audit.verdict}
          bullsWins={audit.bulls_wins}
          bearsWins={audit.bears_wins}
          avgDecisiveness={audit.avg_decisiveness}
        />

        {/* One-sentence rationale */}
        <p
          className="text-sm leading-relaxed text-center max-w-2xl mx-auto"
          style={{ color: 'hsl(var(--gb-text))' }}
          data-testid="verdict-summary"
        >
          {audit.verdict_summary}
        </p>

        {/* Win/loss bar */}
        <div className="max-w-xl mx-auto">
          <VerdictBar bullsWins={audit.bulls_wins} bearsWins={audit.bears_wins} />
        </div>

        {/* Directional warning (if soft sweep) */}
        {audit.is_directional && <DirectionalNote />}

        {/* Stat strip — avg decisiveness + dimension breakdown */}
        <StatStrip
          avgDecisiveness={audit.avg_decisiveness}
          dimensionBreakdown={audit.dimension_breakdown}
        />

        {/* Per-argument records table */}
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="w-full text-left" data-testid="audit-table">
            <thead>
              <tr
                className="text-[10px] uppercase tracking-wider"
                style={{ color: 'hsl(var(--gb-text-muted))' }}
              >
                <th className="px-3 py-2 font-semibold">#</th>
                <th className="px-3 py-2 font-semibold">Side</th>
                <th className="px-3 py-2 font-semibold">Record</th>
                <th className="px-3 py-2 font-semibold">Argument</th>
                <th className="px-3 py-2 font-semibold">Strongest evidence (verbatim)</th>
              </tr>
            </thead>
            <tbody>
              {(audit.bulls || []).map((rec) => (
                <RecordRow key={`bull-${rec.number}`} record={rec} sideColor={bullColor} />
              ))}
              {(audit.bears || []).map((rec) => (
                <RecordRow key={`bear-${rec.number}`} record={rec} sideColor={bearColor} />
              ))}
            </tbody>
          </table>
        </div>

        <p
          className="text-[10px] leading-relaxed text-center pt-2 border-t"
          style={{
            color: 'hsl(var(--gb-text-muted))',
            borderColor: 'hsl(var(--gb-border))',
          }}
        >
          Each of the 5 bull arguments is matched 1-on-1 against each of the 5 bear arguments
          (25 matchups total). Each matchup is decided on the most decisive of four dimensions —
          source primacy · recency · magnitude–evidence fit · counterfactual specificity — and
          rated 1 (close call) to 3 (decisive). Each dimension must decide at least 4 matchups.
        </p>
      </div>
    </section>
  );
};

export default CredibilityAudit;
